import { MouseEvent, useEffect, useState } from 'react';
import { Form, Radio } from 'antd';

import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import Image from 'global/Image';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import useMultiPortfolioStore from 'lib/stores/multiPorto';
import usePortfolioForm from 'features/multi-porto/hooks/usePortfolioCreation';
import { SubAccountPurpose } from 'global/Securities/types/api/multiPorto';

import {
  CharCount,
  Container,
  InputStyled,
  NextButton,
  InputError,
  StyledRadioGroup,
} from './create.styled';

const CreatePortoOnboarding = ({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [form] = Form.useForm();
  const [purpose, setPurpose] = useState<SubAccountPurpose>(undefined);

  const purposesData = useMultiPortfolioStore((s) => s.subAccountsPurposeData);
  const purposeList = purposesData.data?.purposes ?? [];
  const getPurposes = useMultiPortfolioStore((s) => s.getPurposes);
  const setPortfolioForm = useMultiPortfolioStore((s) => s.setPortfolioForm);

  const { portfolioName, error, handlePortfolioNameChange, resetForm } =
    usePortfolioForm();

  const handleReset = () => {
    resetForm();
    setPurpose(undefined);
    form.resetFields();
  };

  const handleSubmit = () => {
    onSubmit();
    setPortfolioForm({
      portfolio_name: portfolioName.trim(),
      tnc: {
        feature_id: '',
        version: 0,
      },
      purpose,
    });
    handleReset();
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
  };

  useEffect(() => {
    if (props.visible) getPurposes();
  }, [props.visible, getPurposes]);

  return (
    <BaseSecuritiesModal
      title="Create New Portfolio"
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container onClick={(e) => e.stopPropagation()}>
        <Form form={form} layout="vertical">
          <Form.Item name="portfolio" label="Portfolio Name">
            <InputStyled
              value={portfolioName}
              onChange={handlePortfolioNameChange}
              maxLength={20}
              placeholder="Input Portfolio Name"
              suffix={
                <CharCount isErrored={!!error}>
                  {portfolioName.length}/20
                </CharCount>
              }
              isErrored={!!error}
            />
            {error && (
              <InputError>
                <Image src="/icon/alert/warning.svg" alt="Error" />
                <span>{error}</span>
              </InputError>
            )}
          </Form.Item>

          <Form.Item name="purpose" label="Purpose" initialValue={0}>
            <StyledRadioGroup
              onChange={(e) => setPurpose(e.target.value)}
              value={purpose}
              className="radio-group"
            >
              {purposeList.map((item) => (
                <Radio key={item.type} value={item.type}>
                  {item.text}
                </Radio>
              ))}
            </StyledRadioGroup>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <NextButton
              onClick={handleSubmit}
              type="primary"
              disabled={!portfolioName || !!error || !purpose}
            >
              Continue
            </NextButton>
          </Form.Item>
        </Form>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default CreatePortoOnboarding;
